import { ComponentProps, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { IconPlus } from '@tabler/icons-react';

import {
  Button,
  IconButton,
  MultiStringInput,
  SelectDropdown,
  Tooltip,
  Typography,
} from '@zendesk/forethought-elements';
import { getOperatorOptions } from '../dashboard-pages/solve-insights/cards/helpers';
import { dateRangeToTimestamp } from '../dashboard-pages/solve-insights/helpers';
import { useNormalizeChannelsForBuildQueryFromMultiSelected } from '../dashboard-pages/solve-insights/hooks/useNormalizeChannels';
import deleteIcon from 'src/assets/images/delete-icon.svg';
import { useStateParams } from 'src/hooks/hooks';
import { buildQueryFromMultiSelected } from 'src/pages/intent-conversation-analytics/hooks/useConversations';
import { DeleteIcon } from 'src/pages/workflow-builder-edit/conditions/ConditionEditor';
import {
  getOperator,
  isValuelessOperator,
} from 'src/pages/workflow-builder-edit/conditions/conditionEditorHelpers';
import { checkboxTypeValueOptions } from 'src/pages/workflow-builder-edit/conditions/constants';
import { ConditionOperators } from 'src/pages/workflow-builder-edit/types/canvasComponentTypes';
import { useGetEventConfigurationsQuery } from 'src/services/custom-events';
import { EventPropertyType } from 'src/services/custom-events/types';
import { useGetCustomEventPropertyValuesQuery } from 'src/services/insight-cards';
import {
  BooleanQueryOperator,
  EventChartProperty,
  FilterQueryOperator,
  InsightCvFilterExpression,
} from 'src/services/insight-cards/types';
import { DateRange } from 'src/types/types';
import { ConversationChannel } from 'src/types/workflowBuilderAPITypes';
import { listDeserialize, listSerialize } from 'src/utils/discover/helpers';
import onKeyDownOnlyNumbers from 'src/utils/onKeyDownOnlyNumbers';

const defaultFilters = [
  {
    boolean_operator: 'and',
    expression: {
      data_type: 'default',
      expression_type: 'filter',
      field: '',
      negate: false,
      operator: '=',
      values: [],
    },
    field_type: 'event',
  },
] as const satisfies InsightCvFilterExpression[];

export default function EventFilterBuilder({
  filters,
  onChange,
  uiDateRange,
}: {
  filters: EventChartProperty[];
  onChange: (value: EventChartProperty[]) => void;
  uiDateRange: DateRange;
}) {
  const { t } = useTranslation('discover');
  const { data: eventConfigurations = [] as const } =
    useGetEventConfigurationsQuery({
      draft_mode: 'any',
    });

  const eventOptions = eventConfigurations?.map(eventConfiguration => ({
    label: eventConfiguration.name,
    value: eventConfiguration.event_configuration_id,
  }));

  const [channels] = useStateParams<string[]>({
    deserialize: listDeserialize,
    initialState: [],
    paramsName: 'channels',
    serialize: listSerialize,
  });

  const normalizeChannelsForBuildQueryFromMultiSelected =
    useNormalizeChannelsForBuildQueryFromMultiSelected();

  const channelsForQuery = (buildQueryFromMultiSelected(
    normalizeChannelsForBuildQueryFromMultiSelected(channels),
  )?.channels?.split(',') || []) as ConversationChannel[];

  const handleAddFilter = useCallback(() => {
    onChange([
      ...filters,
      {
        filters: defaultFilters,
        property_id: eventConfigurations?.[0]?.event_configuration_id || '',
        property_property_id: null,
        type: 'event',
      },
    ]);
  }, [eventConfigurations, filters, onChange]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Typography variant='font14Bold'>
        {t('eventFilterBuilder.title')}
      </Typography>
      <Button
        onClick={handleAddFilter}
        startIcon={<IconPlus fontSize='small' />}
        variant='secondary'
      >
        {t('eventFilterBuilder.addButton')}
      </Button>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {filters.map((property, propertyIndex) => {
          const defaultedFilters = property.filters || [];

          const eventConfiguration = eventConfigurations.find(
            eventConfiguration =>
              eventConfiguration.event_configuration_id ===
              property.property_id,
          );

          const enumerator = propertyIndex + 1;

          return (
            <Box
              key={propertyIndex}
              sx={{
                display: 'flex',
                gap: 1,
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1,
                  gap: 1,
                }}
              >
                <SelectDropdown
                  aria-label={t('eventFilterBuilder.eventAriaLabel', {
                    number: enumerator,
                  })}
                  error={
                    !property.property_id &&
                    t('eventFilterBuilder.eventRequired')
                  }
                  id={`event-${propertyIndex}`}
                  onChange={event => {
                    const selectedValue = event.target.value;
                    const newFilters = structuredClone(filters);
                    newFilters[propertyIndex] = {
                      ...newFilters[propertyIndex],
                      filters: defaultFilters,
                      property_id: selectedValue,
                      property_property_id: null,
                      type: 'event',
                    };
                    onChange(newFilters);
                  }}
                  options={eventOptions}
                  placeholder={t('eventFilterBuilder.eventPlaceholder')}
                  value={property.property_id}
                />
                {defaultedFilters.map((filter, filterIndex) => (
                  <>
                    <SelectDropdown
                      aria-label={t('eventFilterBuilder.propertyAriaLabel', {
                        filterNumber: filterIndex + 1,
                        itemNumber: enumerator,
                      })}
                      error={
                        !filter.expression.field &&
                        t('eventFilterBuilder.propertyRequired')
                      }
                      id={`property-${propertyIndex}-${filterIndex}`}
                      onChange={event => {
                        const updatedProperties = [...filters];
                        updatedProperties[propertyIndex] = {
                          ...updatedProperties[propertyIndex],
                          filters: defaultedFilters.map((f, idx) =>
                            idx === filterIndex
                              ? {
                                  ...f,
                                  expression: {
                                    ...f.expression,
                                    field: event.target.value,
                                    negate: false,
                                    operator: '=',
                                    values: [],
                                  },
                                }
                              : f,
                          ),
                        };

                        onChange(updatedProperties);
                      }}
                      options={(eventConfiguration?.properties || []).map(
                        property => ({
                          label: property.label,
                          value: property.property_id,
                        }),
                      )}
                      placeholder={t('eventFilterBuilder.propertyPlaceholder')}
                      value={filter.expression.field}
                    />
                    <SelectDropdown
                      aria-label={t('eventFilterBuilder.operatorAriaLabel', {
                        filterNumber: filterIndex + 1,
                        itemNumber: enumerator,
                      })}
                      id={`operator-${propertyIndex}-${filterIndex}`}
                      onChange={event => {
                        const updatedProperties = [...filters];
                        updatedProperties[propertyIndex] = {
                          ...updatedProperties[propertyIndex],
                          filters: defaultedFilters.map((f, idx) =>
                            idx === filterIndex
                              ? {
                                  ...f,
                                  expression: {
                                    ...f.expression,
                                    negate: (
                                      [
                                        'is not',
                                        'does not contain',
                                        'does not contain one of',
                                        'is not empty',
                                      ] as const satisfies ConditionOperators[]
                                    )
                                      // @ts-expect-error False error
                                      .includes(event.target.value),
                                    operator:
                                      (
                                        {
                                          // @ts-expect-error Types are messed up
                                          between: 'between',
                                          contains: 'contains',
                                          'contains one of': 'contains_one_of',
                                          'contains one of (exact)':
                                            'contains_one_of_delimited',
                                          'does not contain': 'contains',
                                          'does not contain one of':
                                            'contains_one_of',
                                          'does not contain one of (exact)':
                                            'contains_one_of_delimited',
                                          'greater than': '>',
                                          is: '=',
                                          'is empty': 'empty',
                                          'is not': '=',
                                          'is not empty': 'empty',
                                          'less than': '<',
                                        } as const satisfies Record<
                                          ConditionOperators,
                                          FilterQueryOperator
                                        >
                                      )[event.target.value] || '=',
                                    values: [],
                                  } as const,
                                }
                              : f,
                          ),
                        };

                        onChange(updatedProperties);
                      }}
                      options={getOperatorOptions(
                        eventConfiguration?.properties.find(
                          property =>
                            property.property_id === filter.expression.field,
                        )?.type || 'text',
                      )}
                      placeholder={t('eventFilterBuilder.operatorPlaceholder')}
                      value={getOperator(filter.expression)}
                    />
                    {!isValuelessOperator(getOperator(filter.expression)) && (
                      <EventFilterValueSelector
                        channels={channelsForQuery}
                        dateRange={uiDateRange}
                        defaultedFilters={defaultedFilters}
                        enumerator={enumerator}
                        filter={filter}
                        filterIndex={filterIndex}
                        filters={filters}
                        // `key` has to be compound:
                        // - `filter.expression.field` is needed to reset RTK Query
                        // - `filterIndex` is needed because the same field
                        // can be used multiple times
                        key={`${filter.expression.field}-${filterIndex}`}
                        onChange={onChange}
                        propertyIndex={propertyIndex}
                        type={
                          eventConfiguration?.properties.find(
                            property =>
                              property.property_id === filter.expression.field,
                          )?.type || 'text'
                        }
                      />
                    )}
                  </>
                ))}
                {filters.length > 0 && propertyIndex !== filters.length - 1 && (
                  <Box
                    sx={{
                      marginBottom: '32px',
                      marginTop: '24px',
                      width: '81px',
                    }}
                  >
                    <SelectDropdown
                      disabled={propertyIndex > 0}
                      id={`select-boolean-operator-${propertyIndex}`}
                      onChange={event => {
                        const booleanOperator = event.target
                          .value as BooleanQueryOperator;
                        const updatedProperties = filters.map(filter => ({
                          ...filter,
                          filters: filter.filters?.map(item => ({
                            ...item,
                            boolean_operator: booleanOperator,
                          })),
                        }));

                        onChange(updatedProperties);
                      }}
                      options={[
                        {
                          label: t('eventFilterBuilder.booleanOperator.and'),
                          value: 'and',
                        },
                        {
                          label: t('eventFilterBuilder.booleanOperator.or'),
                          value: 'or',
                        },
                      ]}
                      value={property.filters?.[0]?.boolean_operator || 'and'}
                    />
                  </Box>
                )}
              </Box>
              <Box>
                <Tooltip
                  tooltipContent={t('eventFilterBuilder.removeEventAriaLabel', {
                    number: enumerator,
                  })}
                >
                  <IconButton
                    aria-label={t('eventFilterBuilder.removeEventAriaLabel', {
                      number: enumerator,
                    })}
                    onClick={() => {
                      const updatedProperties = [...filters];
                      updatedProperties.splice(propertyIndex, 1);

                      onChange(updatedProperties);
                    }}
                    style={{ marginTop: '4px' }}
                    variant='ghost'
                  >
                    <DeleteIcon disabled={false} src={deleteIcon} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function EventFilterValueSelector({
  channels,
  dateRange,
  defaultedFilters,
  enumerator,
  filter,
  filterIndex,
  filters,
  onChange,
  propertyIndex,
  type,
}: {
  channels: ConversationChannel[];
  dateRange: DateRange;
  defaultedFilters: InsightCvFilterExpression[];
  enumerator: string | number;
  filter: InsightCvFilterExpression;
  filterIndex: number;
  filters: EventChartProperty[];
  onChange: ComponentProps<typeof EventFilterBuilder>['onChange'];
  propertyIndex: number;
  type: EventPropertyType;
}) {
  const { t } = useTranslation('discover');
  const { end_timestamp, start_timestamp } = dateRangeToTimestamp(dateRange);

  const { data: customEventPropertyValues } =
    useGetCustomEventPropertyValuesQuery(
      {
        channels,
        end: end_timestamp,
        property_id: filter.expression.field,
        start: start_timestamp,
      },
      { skip: !filter.expression.field || type === 'boolean' },
    );

  const handleChange = useCallback(
    (values: string[]) => {
      const updatedProperties = [...filters];
      updatedProperties[propertyIndex] = {
        ...updatedProperties[propertyIndex],
        filters: defaultedFilters.map((f, idx) =>
          idx === filterIndex
            ? {
                ...f,
                expression: {
                  ...f.expression,
                  values,
                },
              }
            : f,
        ),
      };

      onChange(updatedProperties);
    },
    [defaultedFilters, filters, filterIndex, propertyIndex, onChange],
  );

  const handleBooleanChange: ComponentProps<typeof SelectDropdown>['onChange'] =
    useCallback(
      event => {
        handleChange([event.target.value as string]);
      },
      [handleChange],
    );

  const handleStringOrNumberChange = useCallback(
    (_: unknown, values: string[]) => {
      handleChange(values);
    },
    [handleChange],
  );

  if (type === 'boolean') {
    return (
      <SelectDropdown
        aria-label={t('eventFilterBuilder.valueAriaLabel', {
          filterNumber: filterIndex + 1,
          itemNumber: enumerator,
        })}
        error={
          filter.expression.values[0] === undefined &&
          t('eventFilterBuilder.valueRequired')
        }
        id={`value-${propertyIndex}-${filterIndex}`}
        onChange={handleBooleanChange}
        options={checkboxTypeValueOptions}
        value={filter.expression.values[0] as string}
      />
    );
  }

  return (
    <MultiStringInput
      aria-label={t('eventFilterBuilder.valueAriaLabel', {
        filterNumber: filterIndex + 1,
        itemNumber: enumerator,
      })}
      onChange={handleStringOrNumberChange}
      options={customEventPropertyValues?.data || []}
      placeholder={t('eventFilterBuilder.valuePlaceholder')}
      textFieldProps={{
        error: filter.expression.values.length === 0,
        helperText:
          filter.expression.values.length === 0
            ? t('eventFilterBuilder.valueRequired')
            : undefined,
        onKeyDown: type === 'number' ? onKeyDownOnlyNumbers : undefined,
        sx: { '.MuiFormHelperText-root': { marginInline: 0 } },
      }}
      value={filter.expression.values.map(String)}
    />
  );
}
